import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Users, BookOpen, DollarSign, Award, TrendingUp, BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CourseAnalytics = ({ sessionToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/courses/admin/analytics?session_token=${sessionToken}`
      );
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="analytics-loading">
        <div className="w-6 h-6 border-2 border-golden border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-gray-500 text-center py-8">Unable to load analytics.</p>;
  }

  const { overview, courses } = analytics;

  return (
    <div className="space-y-6" data-testid="course-analytics">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="analytics-overview">
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4 text-center">
            <BookOpen className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{overview.total_courses}</p>
            <p className="text-xs text-gray-400">Total Courses</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4 text-center">
            <Users className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{overview.total_enrollments}</p>
            <p className="text-xs text-gray-400">Total Enrollments</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4 text-center">
            <DollarSign className="w-5 h-5 text-golden mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">
              {overview.total_revenue.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-gray-400">Course Revenue</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4 text-center">
            <Award className="w-5 h-5 text-purple-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{overview.total_completed}</p>
            <p className="text-xs text-gray-400">Completions</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-700">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-5 h-5 text-orange-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{overview.overall_completion_rate}%</p>
            <p className="text-xs text-gray-400">Completion Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Per-Course Table */}
      <Card className="bg-gray-900 border-gray-700">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-golden" />
            <h3 className="text-sm font-semibold text-white">Per-Course Breakdown</h3>
          </div>
          {courses.length === 0 ? (
            <p className="text-gray-500 text-center py-8 text-sm">No courses yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="analytics-table">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400 text-xs">
                    <th className="text-left px-4 py-2.5">Course</th>
                    <th className="text-center px-3 py-2.5">Status</th>
                    <th className="text-center px-3 py-2.5">Enrolled</th>
                    <th className="text-center px-3 py-2.5">Completed</th>
                    <th className="px-3 py-2.5 text-center">Avg Progress</th>
                    <th className="text-right px-4 py-2.5">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((c) => (
                    <tr key={c.course_id} className="border-b border-gray-800 hover:bg-gray-800/40 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium text-sm leading-tight">{c.title}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{c.total_lessons} lessons</p>
                      </td>
                      <td className="text-center px-3 py-3">
                        <Badge
                          className={c.is_published
                            ? 'bg-green-500/20 text-green-400 border-green-500/30 text-[10px]'
                            : 'bg-gray-500/20 text-gray-400 border-gray-500/30 text-[10px]'}
                        >
                          {c.is_published ? 'Published' : 'Draft'}
                        </Badge>
                      </td>
                      <td className="text-center px-3 py-3 text-white font-medium">{c.enrollments}</td>
                      <td className="text-center px-3 py-3">
                        <span className="text-white font-medium">{c.completed}</span>
                        {c.enrollments > 0 && (
                          <span className="text-gray-500 text-xs ml-1">({c.completion_rate}%)</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2 justify-center">
                          <Progress value={c.avg_progress} className="h-1.5 w-16 bg-gray-700" />
                          <span className="text-xs text-gray-400 w-10 text-right">{c.avg_progress}%</span>
                        </div>
                      </td>
                      <td className="text-right px-4 py-3 text-golden font-medium">
                        {c.revenue > 0
                          ? c.revenue.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CourseAnalytics;
